// Mixin to render status color on status text across the system
import { VIEW_FORM_STATUS, get_headers, get_project_id } from '@/utils/constants/constants.js';
import axios from 'axios';

let shared_status_colors = {};
let shared_status_definitions = [];
let shared_status_colors_loaded = false;
let shared_status_colors_request = null;

export const statusMixin = {
  data: () => ({
    global_status_colors: {},
    is_status_colors_loaded: false,
  }),
  
  methods: {
    sync_status_colors_from_shared_cache() {
      if (!shared_status_colors_loaded) return;

      this.global_status_colors = { ...shared_status_colors };
      this.is_status_colors_loaded = true;
    },

    get_global_status_definitions() {
      return Array.isArray(shared_status_definitions)
        ? [...shared_status_definitions]
        : [];
    },

    async fetch_global_status_colors() {
      this.sync_status_colors_from_shared_cache();

      // Reuse shared colors across every component instance.
      if (shared_status_colors_loaded && Object.keys(shared_status_colors).length > 0) {
        return shared_status_colors;
      }

      // If a request is already running, wait for the same promise instead of firing again.
      if (shared_status_colors_request) {
        return shared_status_colors_request.then((colors) => {
          this.global_status_colors = { ...colors };
          this.is_status_colors_loaded = true;
          return colors;
        });
      }

      // If already loaded locally, return immediately
      if (this.is_status_colors_loaded && Object.keys(this.global_status_colors).length > 0) {
        return this.global_status_colors;
      }

      const payload = { project_id: get_project_id() };

      shared_status_colors_request = axios.get(VIEW_FORM_STATUS, {
        headers: get_headers(),
        params: payload
      })
      .then(response => {
        // Create one shared mapping of status names to colors for the whole app.
        shared_status_colors = {};
        shared_status_definitions = Array.isArray(response?.data?.data)
          ? [...response.data.data]
          : [];

        shared_status_definitions.forEach((status) => {
          const status_name = status.status.replace(/_/g, ' ');
          Reflect.set(shared_status_colors, status_name, status.color_code || status.color);
        });

        shared_status_colors_loaded = true;
        this.global_status_colors = { ...shared_status_colors };
        this.is_status_colors_loaded = true;
        return shared_status_colors;
      })
      .catch(() => {
        // Return empty object if request fails
        return {};
      })
      .finally(() => {
        shared_status_colors_request = null;
      });

      return shared_status_colors_request;
    },

    get_status_color(status_name) {
      if (!status_name) {
        return;
      }
      
      // Try to find exact match first (safe access)
      let color = Reflect.get(this.global_status_colors, status_name);

      // If not found, try case-insensitive match
      if (!color) {
        const normalized_status = status_name.toLowerCase();
        const found_key = Object.keys(this.global_status_colors).find(
          key => key.toLowerCase() === normalized_status
        );

        // Securely access only if the key truly exists on the object
        if (found_key && Object.hasOwn(this.global_status_colors, found_key)) {
          color = Reflect.get(this.global_status_colors, found_key);
        } else {
          color = '#000000';
        }
      }
      
      return color;
    },

    // Method to ensure status colors are loaded
    async ensure_status_colors_loaded() {
      this.sync_status_colors_from_shared_cache();
      if (!this.is_status_colors_loaded) {
        await this.fetch_global_status_colors();
      }
    }
  },

  // Auto-fetch status colors when component is created
  async created() {
    await this.ensure_status_colors_loaded();
  }
}
